// Admin Authentication Hook
// Manages admin auth state, login/logout, session timeout

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@core/store';
import { adminLoginSuccess, adminLogout as adminLogoutAction, updateAdminProfile } from '@core/store/slices/authSlice';
import { adminAuthApi } from '../api/adminAuthApi';
import type {
    AdminUser,
    AdminLoginRequest,
    AdminLoginResponse,
} from '../types/admin-auth.types';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_RESET_INTERVAL_MS = 60000; // 1 minute

export const useAdminAuth = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentAdmin = useAppSelector((state) => state.auth.adminUser) as AdminUser | null;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sessionTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    const isAdminAuthenticated = useAppSelector((state) => state.auth.isAdminAuthenticated);

    // Session timeout management
    const startSessionTimeout = useCallback(() => {
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
        }

        sessionTimerRef.current = setTimeout(() => {
            alert('Your admin session has expired. Please login again.');
            clearSession();
        }, SESSION_TIMEOUT_MS);
    }, []);

    const stopSessionTimeout = useCallback(() => {
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
            sessionTimerRef.current = null;
        }
    }, []);

    const resetSessionTimeout = useCallback(() => {
        const now = Date.now();
        // Only reset if more than 1 minute since last activity
        if (now - lastActivityRef.current > ACTIVITY_RESET_INTERVAL_MS) {
            lastActivityRef.current = now;
            if (isAdminLoggedIn()) {
                startSessionTimeout();
            }
        }
    }, [startSessionTimeout]);

    // Track user activity
    useEffect(() => {
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

        events.forEach((event) => {
            document.addEventListener(event, resetSessionTimeout, { passive: true });
        });

        return () => {
            events.forEach((event) => {
                document.removeEventListener(event, resetSessionTimeout);
            });
        };
    }, [resetSessionTimeout]);

    // Start session timeout on mount if logged in
    useEffect(() => {
        if (isAdminLoggedIn()) {
            startSessionTimeout();
        }
        return () => stopSessionTimeout();
    }, [startSessionTimeout, stopSessionTimeout]);

    const getAdminAccessToken = (): string | null => {
        return localStorage.getItem('admin_token');
    };

    const isAdminLoggedIn = (): boolean => {
        // Use the validated Redux state (which now checks token expiration)
        return isAdminAuthenticated && !!currentAdmin;
    };

    const setCurrentAdminData = (admin: AdminUser): void => {
        dispatch(updateAdminProfile(admin));
    };

    const handleLoginSuccess = (response: AdminLoginResponse): void => {
        if (response.accessToken && response.admin) {
            // Sync with Redux (which handles localStorage)
            dispatch(adminLoginSuccess({
                adminUser: response.admin,
                token: response.accessToken,
                refreshToken: response.refreshToken
            } as any));

            startSessionTimeout();
        }
    };

    const clearSession = (): void => {
        dispatch(adminLogoutAction()); // Sync with Redux (which handles localStorage)
        stopSessionTimeout();
        navigate('/dashboard/admin-login');
    };

    const login = async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await adminAuthApi.login(credentials);

            if (response.success) {
                handleLoginSuccess(response);
                return response;
            } else {
                const errorMsg = response.message || 'Login failed';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (err: any) {
            let errorMessage = 'Login failed. Please try again.';

            if (err.response) {
                if (err.response.status === 401) {
                    errorMessage = 'Invalid admin credentials';
                } else if (err.response.status === 403) {
                    errorMessage = err.response.data?.message || 'Account is locked. Contact software provider.';
                } else {
                    errorMessage = err.response.data?.message || errorMessage;
                }
            }

            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await adminAuthApi.logout();
        } finally {
            clearSession();
        }
    };

    const hasRole = (roles: string[]): boolean => {
        return currentAdmin ? roles.includes(currentAdmin.role) : false;
    };

    const hasPermission = (permission: string): boolean => {
        if (!currentAdmin) return false;
        if (currentAdmin.role === 'SuperAdmin') return true;
        return currentAdmin.permissions?.[permission] === true;
    };

    return {
        currentAdmin,
        isLoading,
        error,
        login,
        logout,
        isAdminLoggedIn,
        hasRole,
        hasPermission,
        getAdminAccessToken,
        setCurrentAdminData,
        clearSession,
    };
};
